// ─────────────────────────────────────────────────────────────────
//  PatientProfile.jsx  —  v3  "Clinical Clarity" enhanced
//  • Hero identity card on the deep GRAD_HERO gradient (matches Overview)
//    with grid texture, cyan glow, avatar ring + online dot, glass chips
//  • Section cards with gradient icon chips + display-font titles
//  • Refined info fields with hover tint
//  Props unchanged: { patient, decoded }
// ─────────────────────────────────────────────────────────────────
import React from "react";
import { Row, Col, Avatar, Typography } from "antd";
import {
  UserOutlined, PhoneOutlined, MailOutlined,
  CalendarOutlined, EnvironmentOutlined,
  SafetyOutlined, MedicineBoxOutlined, TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  G700, G100, G50, GRAY6, GRAY7,
  SLATE, MUTED, WHITE,
  GRAD_BRAND,
  RADIUS_LG, RADIUS_SM, RADIUS_PILL,
  SHADOW_SM, SHADOW_BLUE,
  FF_DISPLAY, FF_BODY, FF_MONO,
  TRANSITION_SM,
} from "./patientTokens";

const { Title, Text } = Typography;
const PHP_APP_URL = process.env.REACT_APP_PHP_URL || "https://hmsv3atlantispediatrichospital.com/";

// ── Field row inside a section ─────────────────────────────────
function InfoField({ icon, label, value, mono = false, accent }) {
  if (!value && value !== 0) return null;
  return (
    <div
      style={{
        display:      "flex",
        alignItems:   "flex-start",
        gap:          12,
        padding:      "10px 8px",
        margin:       "0 -8px",
        borderRadius: 8,
        borderBottom: `1px solid ${GRAY7}`,
        transition:   `background ${TRANSITION_SM}`,
      }}
      onMouseEnter={e => (e.currentTarget.style.background = G50)}
      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
    >
      <div style={{
        width: 32, height: 32,
        borderRadius: RADIUS_SM,
        background: accent ? `${accent}14` : G50,
        border: `1px solid ${accent ? `${accent}22` : G100}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: accent || G700,
        fontSize: 14, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text style={{
          fontSize: 10, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.7px",
          color: MUTED, display: "block", fontFamily: FF_BODY,
        }}>
          {label}
        </Text>
        <Text style={{
          fontSize: 13.5, color: SLATE, fontWeight: 500,
          fontFamily: mono ? FF_MONO : FF_BODY,
          wordBreak: "break-word",
        }}>
          {value}
        </Text>
      </div>
    </div>
  );
}

// ── Card wrapper ───────────────────────────────────────────────
function ProfileCard({ icon, title, children }) {
  return (
    <div className="pd-card-hover" style={{
      background:   WHITE,
      border:       `1px solid ${GRAY6}`,
      borderRadius: RADIUS_LG,
      boxShadow:    SHADOW_SM,
      overflow:     "hidden",
      height:       "100%",
    }}>
      <div style={{
        padding:      "13px 18px",
        borderBottom: `1px solid ${GRAY7}`,
        background:   G50,
        display:      "flex",
        alignItems:   "center",
        gap:          10,
      }}>
        <div style={{
          width: 30, height: 30,
          borderRadius: 9,
          background: GRAD_BRAND,
          boxShadow: SHADOW_BLUE,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: WHITE, fontSize: 14, flexShrink: 0,
        }}>
          {icon}
        </div>
        <Text strong style={{
          color: SLATE, fontSize: 13.5,
          fontFamily: FF_DISPLAY, letterSpacing: "-0.2px",
        }}>
          {title}
        </Text>
      </div>
      <div style={{ padding: "4px 18px 14px" }}>
        {children}
      </div>
    </div>
  );
}

// ── Glass chip for the hero ────────────────────────────────────
const HeroChip = ({ children, solid, color }) => (
  <span style={{
    display:      "inline-flex",
    alignItems:   "center",
    background:   solid ? WHITE : "rgba(255,255,255,0.14)",
    border:       solid ? "none" : "1px solid rgba(255,255,255,0.24)",
    backdropFilter: solid ? "none" : "blur(8px)",
    WebkitBackdropFilter: solid ? "none" : "blur(8px)",
    color:        solid ? color : WHITE,
    padding:      "4px 13px",
    borderRadius: RADIUS_PILL,
    fontSize:     11,
    fontWeight:   solid ? 700 : 600,
    fontFamily:   FF_BODY,
    whiteSpace:   "nowrap",
  }}>
    {children}
  </span>
);

// ── MAIN ──────────────────────────────────────────────────────
function PatientProfile({ patient, decoded }) {
  const p = patient || {};

  const displayName = p.full_name || decoded?.fullName || "Patient";
  const prn         = p.prn       || decoded?.prn       || "—";
  const initial     = displayName[0]?.toUpperCase() || "P";
  const photoUrl    = p.photo
    ? `${PHP_APP_URL}/uploads/patients/img/${p.photo}`
    : null;

  const dob = p.date_of_birth
    ? dayjs(p.date_of_birth).format("MMMM D, YYYY")
    : null;
  const age = p.date_of_birth
    ? `${Math.floor((Date.now() - new Date(p.date_of_birth)) / 31557600000)} years`
    : null;

  const bloodColors = {
    "A+":"#ef4444","A-":"#ef4444","B+":"#f97316","B-":"#f97316",
    "AB+":"#8b5cf6","AB-":"#8b5cf6","O+":"#0e5bc2","O-":"#0e5bc2",
  };
  const bloodColor = bloodColors[p.blood_group] || G700;

  return (
    <div style={{ width: "100%" }}>

      {/* ── Header ── */}
      <div className="pd-up" style={{ marginBottom: 20 }}>
        <div className="pd-eyebrow"><UserOutlined /> My Profile</div>
        <Title level={3} style={{
          margin:        "2px 0 4px",
          fontFamily:    FF_DISPLAY,
          fontWeight:    800,
          color:         SLATE,
          letterSpacing: "-0.6px",
          fontSize:      "clamp(1.3rem, 3vw, 1.75rem)",
        }}>
          Personal Information
        </Title>
        <Text style={{ color: MUTED, fontSize: 13, fontFamily: FF_BODY }}>
          Your registered health profile
        </Text>
      </div>

      {/* ── Hero identity card (matches Overview hero) ── */}
      <div className="pd-hero pd-up pd-up-1" style={{ marginBottom: 20 }}>
        <div className="pd-hero-grid" />

        {/* Heartbeat trace along the bottom, echoing the Overview */}
        <svg
          viewBox="0 0 900 60" preserveAspectRatio="none" aria-hidden="true"
          style={{
            position: "absolute", left: 0, right: 0, bottom: 6,
            width: "100%", height: 46, opacity: 0.4, pointerEvents: "none",
          }}
        >
          <path
            className="pd-ekg-path"
            d="M0,30 L220,30 L245,30 L257,10 L269,50 L281,22 L293,30 L560,30 L585,30 L597,8 L609,52 L621,20 L633,30 L900,30"
            fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>

        <div style={{
          display: "flex", alignItems: "center", gap: 22,
          flexWrap: "wrap", padding: "28px 28px 30px", position: "relative",
        }}>
          {/* Avatar with ring + status dot */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <Avatar
              size={84}
              src={photoUrl || undefined}
              style={{
                background: "rgba(255,255,255,0.18)",
                fontSize:   32,
                fontWeight: 700,
                border:     "3px solid rgba(255,255,255,0.45)",
                boxShadow:  "0 8px 24px rgba(0,0,0,0.22)",
              }}
            >
              {initial}
            </Avatar>
            <span style={{
              position: "absolute", bottom: 4, right: 4,
              width: 16, height: 16, borderRadius: "50%",
              background: "#22A66A", border: "3px solid rgba(255,255,255,0.9)",
            }} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Text style={{
              fontSize:      "clamp(1.25rem, 3vw, 1.65rem)",
              fontWeight:    800,
              color:         WHITE,
              display:       "block",
              letterSpacing: "-0.5px",
              fontFamily:    FF_DISPLAY,
              lineHeight:    1.2,
            }}>
              {displayName}
            </Text>
            <Text style={{
              color: "rgba(255,255,255,0.68)",
              fontSize: 12, fontFamily: FF_MONO,
              display: "block", marginTop: 4, letterSpacing: "0.3px",
            }}>
              PRN · {prn}
            </Text>
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              {p.gender      && <HeroChip>{p.gender}</HeroChip>}
              {age           && <HeroChip>{age}</HeroChip>}
              {p.blood_group && <HeroChip solid color={bloodColor}>🩸 {p.blood_group}</HeroChip>}
              {p.nationality && <HeroChip>{p.nationality}</HeroChip>}
            </div>
          </div>
        </div>
      </div>

      {/* ── Cards grid ── */}
      <Row gutter={[16, 16]}>

        {/* Personal */}
        <Col xs={24} md={12}>
          <div className="pd-up pd-up-1" style={{ height: "100%" }}>
            <ProfileCard icon={<UserOutlined />} title="Personal Details">
              <InfoField icon={<UserOutlined />}     label="Full Name"      value={displayName} />
              <InfoField icon={<UserOutlined />}     label="Patient PRN"    value={prn}          mono />
              <InfoField icon={<CalendarOutlined />} label="Date of Birth"  value={dob} />
              <InfoField icon={<CalendarOutlined />} label="Age"            value={age} />
              <InfoField icon={<TeamOutlined />}     label="Gender"         value={p.gender} />
              <InfoField icon={<SafetyOutlined />}   label="Nationality"    value={p.nationality} />
              <InfoField icon={<TeamOutlined />}     label="Marital Status" value={p.marital_status} />
              <InfoField icon={<TeamOutlined />}     label="Occupation"     value={p.occupation} />
              <InfoField icon={<TeamOutlined />}     label="Religion"       value={p.religion} />
            </ProfileCard>
          </div>
        </Col>

        {/* Contact */}
        <Col xs={24} md={12}>
          <div className="pd-up pd-up-2" style={{ height: "100%" }}>
            <ProfileCard icon={<PhoneOutlined />} title="Contact Information">
              <InfoField icon={<PhoneOutlined />}       label="Primary Phone"   value={p.phone}  mono />
              <InfoField icon={<PhoneOutlined />}       label="Secondary Phone" value={p.phone2} mono />
              <InfoField icon={<MailOutlined />}        label="Email"           value={p.email}  mono />
              <InfoField icon={<EnvironmentOutlined />} label="Address"         value={p.address} />
            </ProfileCard>
          </div>
        </Col>

        {/* Medical */}
        <Col xs={24} md={12}>
          <div className="pd-up pd-up-3" style={{ height: "100%" }}>
            <ProfileCard icon={<MedicineBoxOutlined />} title="Medical Information">
              <InfoField icon={<TeamOutlined />}        label="Patient Category" value={p.patient_category_id} />
              <InfoField icon={<SafetyOutlined />}      label="Enrollee No."     value={p.enrollee_no} mono />
              <InfoField icon={<MedicineBoxOutlined />} label="Plan Type"        value={p.plan_type_id} />
              <InfoField
                icon={<SafetyOutlined />}
                label="CDC Status"
                value={p.cdc_status === "y" ? "Active" : "Inactive"}
                accent={p.cdc_status === "y" ? "#1A7F52" : "#C47020"}
              />
            </ProfileCard>
          </div>
        </Col>

        {/* Next of kin */}
        <Col xs={24} md={12}>
          <div className="pd-up pd-up-4" style={{ height: "100%" }}>
            <ProfileCard icon={<TeamOutlined />} title="Next of Kin">
              <InfoField icon={<UserOutlined />}        label="Name"    value={p.nok} />
              <InfoField icon={<PhoneOutlined />}       label="Phone"   value={p.nok_phone} mono />
              <InfoField icon={<EnvironmentOutlined />} label="Address" value={p.nok_address} />
            </ProfileCard>
          </div>
        </Col>

      </Row>

      {/* Registered date footer */}
      {p.created_at && (
        <div style={{
          textAlign: "center", marginTop: 24, padding: "12px 0",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            padding: "5px 14px", borderRadius: RADIUS_PILL,
            background: WHITE, border: `1px solid ${GRAY6}`,
            color: MUTED, fontSize: 11.5, fontFamily: FF_BODY, boxShadow: SHADOW_SM,
          }}>
            <CalendarOutlined style={{ fontSize: 11, color: G700 }} />
            Registered on {dayjs(p.created_at).format("MMMM D, YYYY")}
          </span>
        </div>
      )}
    </div>
  );
}

export default PatientProfile;